import { useCallback } from "react";
import { observer } from "mobx-react-lite";

import PromptEditorContent from "./PromptEditorContent";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { PlaygroundStoreProvider } from "@/components/Playground/playground-store";
import { useAuth } from "@/context/auth-context";
import { usePrompt } from "@/queries";

interface PromptEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  workspaceId?: number;
  existingPromptId?: number;
  existingPromptName?: string;
  existingVersionNumber?: number;
  existingLabel?: string;
  onSaveComplete: (result: { id: number; name: string; version?: number }) => void;
}

const PromptEditorModal = observer((props: PromptEditorModalProps) => {
  const {
    isOpen,
    onClose,
    mode,
    workspaceId,
    existingPromptName,
    existingVersionNumber,
    existingLabel,
    ...rest
  } = props;
  const auth = useAuth();
  const userToken = auth?.userToken || "";

  // Fetch existing prompt data for edit mode
  const promptQuery = usePrompt(userToken, {
    workspace_id: workspaceId || 0,
    prompt_name: mode === "edit" && existingPromptName ? existingPromptName : undefined,
    number: mode === "edit" ? existingVersionNumber : undefined,
    label: mode === "edit" ? existingLabel : undefined,
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  // Show loading state while fetching prompt data in edit mode
  if (mode === "edit" && promptQuery.isLoading && !promptQuery.data) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="flex h-[90vh] max-h-[800px] !w-[90vw] max-w-[1400px] flex-col p-0">
          <div className="flex h-full items-center justify-center">
            <div className="text-gray-500">Loading prompt...</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex h-[90vh] max-h-[800px] !w-[90vw] max-w-[1400px] flex-col p-0"
        shouldShowCloseButton
      >
        {/* Accessibility: provide a DialogTitle for screen readers */}
        <DialogHeader>
          <DialogTitle className="sr-only">
            {mode === "create"
              ? "Create New Prompt"
              : `Edit Prompt: ${existingPromptName || "Prompt"}`}
          </DialogTitle>
          <DialogDescription className="sr-only">Inline prompt editor dialog.</DialogDescription>
        </DialogHeader>
        <PlaygroundStoreProvider
          key={`prompt-editor-${rest.existingPromptId || "new"}-${mode}`}
          value={{
            // @ts-expect-error - TODO: fix this
            prompt_blueprint: mode === "edit" ? promptQuery.data : undefined,
            input_variables:
              // @ts-expect-error - TODO: fix this
              mode === "edit" && promptQuery.data?.input_variables
                ? // @ts-expect-error - TODO: fix this
                  promptQuery.data.input_variables.map((v: string) => ({
                    key: v,
                    value: "",
                  }))
                : undefined,
          }}
        >
          <PromptEditorContent
            {...rest}
            mode={mode}
            workspaceId={workspaceId}
            existingPromptName={existingPromptName}
            existingVersionNumber={existingVersionNumber}
            existingLabel={existingLabel}
            onClose={onClose}
          />
        </PlaygroundStoreProvider>
      </DialogContent>
    </Dialog>
  );
});

export default PromptEditorModal;
